import { computed, inject, Injectable } from '@angular/core';
import type { Locator } from '@readium/shared';
import { extractFileNameWithoutExtension } from '../../helpers/locator';
import { getVisibleTextFragment } from '../../helpers/readium';
import { isBookmark } from '../../helpers/selections';
import type { Bookmark, SelectionResponse } from '../../models/selections';
import { MetadataService } from '../metadata/metadata';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';
import { SelectionsService } from './selections';

@Injectable({
  providedIn: 'root',
})
export class BookmarksService {
  private readonly selectionsService = inject(SelectionsService);
  private readonly metadataService = inject(MetadataService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  readonly bookmarks = computed<Bookmark[]>(() =>
    this.selectionsService
      .selections()
      .filter(isBookmark)
      .toSorted((a, b) => a.progress - b.progress),
  );
  readonly actualLocator = this.selectionsService.actualLocator;
  readonly isFixedLayout = this.metadataService.isFixedLayout;
  readonly currentBookmark = computed(() => {
    const bookmarks = this.bookmarks();
    const actualLocator = this.actualLocator();
    if (!bookmarks.length || !actualLocator) {
      return undefined;
    }
    return this.checkIfBookmarkIsPresent(bookmarks, actualLocator);
  });

  checkIfBookmarkIsPresent(
    bookmarks: Bookmark[],
    actualLocator: Locator,
  ): SelectionResponse | undefined {
    const visibleText = getVisibleTextFragment(
      this.readerNavigatorService.getCurrentFrameElements(),
    );
    return bookmarks.find((selection) => {
      const selectionHref = selection.locator.href;
      const selectionText =
        selection.locator.text?.highlight ??
        ('contextReadingPoint' in selection && selection.contextReadingPoint);

      const isSameChapter =
        extractFileNameWithoutExtension(selectionHref) ===
        extractFileNameWithoutExtension(actualLocator.href);

      const isSelectionTextVisible = this.isFixedLayout()
        ? selection.locator.locations.position === actualLocator.locations.position
        : selectionText && visibleText.includes(selectionText);

      return isSameChapter && isSelectionTextVisible;
    });
  }

  async saveBookmark(): Promise<void> {
    await this.selectionsService.saveSelection({ isBookmark: true });
  }

  async removeBookmark(id: string): Promise<void> {
    await this.selectionsService.removeSelection(id);
  }
}
